import type { EntityInstance, RenderoniEngine } from 'renderoni';
import type { EnvironmentDef } from '../config/environments';
import { BUGGY, DELIVERY, FUEL, PICKUPS } from '../config/tuning';
import { StallTimer, stepFuel } from './fuel';
import { Buggy, TERRAIN_COLLISION_GROUPS, type BuggyReadout } from './buggy';
import { StuntTracker, type StuntEvent } from './stunts';
import { CHUNK_LENGTH, createChunkBody, sampleChunk } from './terrain/chunks';
import { TerrainProfile, type PickupKind, type PickupSpawn } from './terrain/profile';
import { vehicleStats, type VehicleStats } from './upgrades';

export type RunStatus = 'ready' | 'running' | 'ended';
export type EndReason = 'crash' | 'out_of_fuel';

export type RunEvent =
  | { type: 'started' }
  | { type: 'pickup'; kind: PickupKind; id: number; x: number; y: number }
  | { type: 'stunt'; stunt: StuntEvent }
  | { type: 'landed'; airSeconds: number }
  | { type: 'delivery'; metres: number; coins: number }
  | { type: 'chunkAdded'; index: number; heights: Float32Array }
  | { type: 'chunkRemoved'; index: number }
  | { type: 'ended'; reason: EndReason; distance: number };

export interface RunState {
  status: RunStatus;
  endReason: EndReason | null;
  /** Furthest distance reached from the spawn point, in metres. */
  distance: number;
  coins: number;
  fuel: number;
  /** Tank size for this run (the Fuel tank upgrade raises it). */
  fuelCapacity: number;
  flips: number;
  /** Delivery bonuses paid so far (one per DELIVERY.every metres). */
  deliveries: number;
  /** Longest single airtime this run, in seconds. */
  bestAir: number;
  ticks: number;
}

/** Chunk window around the buggy: behind / ahead, in metres. */
const KEEP_BEHIND = 40;
const KEEP_AHEAD = 110;

/**
 * One run on one environment. All rules execute inside Renderoni's fixed tick
 * (prePhysics: input, streaming, drive; postPhysics: contacts and rules), so a run is a pure
 * function of the environment seed and the per-tick throttle sequence.
 * A run is never reset in place: restarting creates a fresh engine (see createSim), because
 * Rapier's handle reuse would otherwise make a restarted run differ slightly from a fresh one.
 */
export class Run {
  readonly terrain: TerrainProfile;
  readonly state: RunState = Run.freshState();
  /** Throttle intent in [-1, 1]; read once per tick (quantized to hundredths, see preTick). */
  throttle = 0;
  /** Ticks spent waiting on the start line before the first throttle (a replay must wait as long). */
  idleTicks = 0;
  /** The quantized throttle used on every running tick: a ghost replays exactly this sequence. */
  readonly inputs: number[] = [];
  buggy!: Buggy;
  readout!: BuggyReadout;
  private readonly events: RunEvent[] = [];
  /** The quantized throttle of the current tick (fuel drain reads the same value the drive used). */
  private appliedThrottle = 0;
  private readonly chunks = new Map<number, true>();
  private readonly collected = new Set<number>();
  private readonly stunts = new StuntTracker();
  private readonly stall = new StallTimer();
  private runEntity!: EntityInstance;
  private readonly disposeSystems: Array<() => void> = [];

  constructor(
    readonly engine: RenderoniEngine,
    readonly env: EnvironmentDef,
    readonly stats: VehicleStats = vehicleStats(),
  ) {
    this.state.fuel = this.state.fuelCapacity = stats.fuelCapacity;
    this.terrain = new TerrainProfile(env);
    this.build();
    this.disposeSystems.push(
      engine.systems.add({ phase: 'prePhysics', update: ({ dt }) => this.preTick(dt) }),
      engine.systems.add({ phase: 'postPhysics', update: ({ dt }) => this.postTick(dt) }),
    );
  }

  static freshState(): RunState {
    return { status: 'ready', endReason: null, distance: 0, coins: 0, fuel: 0, fuelCapacity: 0, flips: 0, deliveries: 0, bestAir: 0, ticks: 0 };
  }

  get spawnX(): number {
    return BUGGY.spawnX;
  }

  /** Events since the last drain, in tick order. Presentation consumes them once. */
  drainEvents(): RunEvent[] {
    return this.events.splice(0, this.events.length);
  }

  isCollected(id: number): boolean {
    return this.collected.has(id);
  }

  activeChunkIndices(): number[] {
    return [...this.chunks.keys()];
  }

  /** Forces the run to end (used by test hooks for the summary state). */
  forceEnd(reason: EndReason): void {
    this.end(reason);
  }

  dispose(): void {
    this.buggy.dispose();
    for (const off of this.disposeSystems) off();
  }

  private build(): void {
    this.streamChunks(BUGGY.spawnX);
    this.buggy = new Buggy(this.engine, BUGGY.spawnX, this.terrain.heightAt(BUGGY.spawnX), this.stats);
    this.runEntity = this.engine.add({ id: 'run', tags: ['run'], state: { ...this.state } });
    this.readout = this.buggy.read();
  }

  private preTick(dt: number): void {
    // Hundredths, so a recorded run replays bit-exactly from a compact integer sequence.
    const throttle = Math.round(Math.max(-1, Math.min(1, this.throttle)) * 100) / 100;
    if (this.state.status === 'ready' && throttle > 0) {
      this.state.status = 'running';
      this.events.push({ type: 'started' });
    }
    if (this.state.status === 'ready') this.idleTicks += 1;
    else if (this.state.status === 'running') this.inputs.push(Math.round(throttle * 100));
    this.streamChunks(this.readout.x);
    if (this.state.status === 'running') {
      const grounded = this.readout.wheelGrounded[0] || this.readout.wheelGrounded[1];
      this.buggy.applyControls(throttle, this.state.fuel > 0, grounded, dt);
      this.appliedThrottle = throttle;
    } else {
      this.buggy.cutPower();
    }
    this.buggy.updateVehicle(dt);
  }

  private postTick(dt: number): void {
    const r = this.buggy.read();
    this.readout = r;
    const s = this.state;
    if (s.status !== 'running') return;
    s.ticks += 1;

    const grounded = r.wheelGrounded[0] || r.wheelGrounded[1];
    s.fuel = stepFuel(
      s.fuel,
      { throttle: this.appliedThrottle, forwardSpeed: r.forwardSpeed },
      dt,
      { capacity: s.fuelCapacity, drainIdle: this.stats.drainIdle, drainThrottle: this.stats.drainThrottle },
    );
    s.distance = Math.max(s.distance, r.x - BUGGY.spawnX);
    while (s.distance >= (s.deliveries + 1) * DELIVERY.every) {
      s.deliveries += 1;
      s.coins += DELIVERY.coins;
      this.events.push({ type: 'delivery', metres: s.deliveries * DELIVERY.every, coins: DELIVERY.coins });
    }

    this.collectPickups(r);

    const wasAirborne = this.stunts.airborne;
    const airSeconds = this.stunts.airSeconds;
    for (const stunt of this.stunts.update(grounded, r.angularVelocityZ, dt)) {
      if (stunt.type === 'flip') {
        s.flips += 1;
        s.coins += stunt.coins;
        s.fuel = Math.min(s.fuelCapacity, s.fuel + stunt.fuel);
      } else {
        s.coins += stunt.coins;
      }
      this.events.push({ type: 'stunt', stunt });
    }
    if (wasAirborne && grounded) {
      s.bestAir = Math.max(s.bestAir, airSeconds);
      this.events.push({ type: 'landed', airSeconds });
    }

    if (r.helmetHit) this.end('crash');
    else if (this.stall.update(s.fuel, r.forwardSpeed, dt)) this.end('out_of_fuel');

    Object.assign(this.runEntity.state, s);
  }

  private collectPickups(r: BuggyReadout): void {
    const radiusSq = PICKUPS.collectRadius * PICKUPS.collectRadius;
    for (const p of this.terrain.pickupsIn(r.x - 3, r.x + 3)) {
      if (this.collected.has(p.id)) continue;
      const dx = p.x - r.x;
      const dy = p.y - r.y;
      if (dx * dx + dy * dy > radiusSq) continue;
      this.collect(p);
    }
  }

  private collect(p: PickupSpawn): void {
    this.collected.add(p.id);
    if (p.kind === 'fuel') this.state.fuel = Math.min(this.state.fuelCapacity, this.state.fuel + FUEL.canAmount);
    else this.state.coins += PICKUPS.coinValue;
    this.events.push({ type: 'pickup', kind: p.kind, id: p.id, x: p.x, y: p.y });
  }

  private end(reason: EndReason): void {
    if (this.state.status === 'ended') return;
    this.state.status = 'ended';
    this.state.endReason = reason;
    this.buggy.cutPower();
    this.events.push({ type: 'ended', reason, distance: this.state.distance });
    Object.assign(this.runEntity.state, this.state);
  }

  private streamChunks(x: number): void {
    const first = Math.max(0, Math.floor((x - KEEP_BEHIND) / CHUNK_LENGTH));
    const last = Math.floor((x + KEEP_AHEAD) / CHUNK_LENGTH);
    for (const index of [...this.chunks.keys()]) if (index < first || index > last) this.removeChunk(index);
    for (let index = first; index <= last; index++) if (!this.chunks.has(index)) this.addChunk(index);
  }

  private addChunk(index: number): void {
    const heights = sampleChunk(this.terrain, index);
    const { body, collider } = createChunkBody(this.engine.native.world, index, heights, this.env.groundFriction);
    collider.setCollisionGroups(TERRAIN_COLLISION_GROUPS);
    this.engine.add({ id: `chunk-${index}`, tags: ['terrain'], native: { rapier: { body } } });
    this.chunks.set(index, true);
    this.events.push({ type: 'chunkAdded', index, heights });
  }

  private removeChunk(index: number): void {
    this.engine.remove(`chunk-${index}`);
    this.chunks.delete(index);
    this.events.push({ type: 'chunkRemoved', index });
  }
}
